import React, { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import NewsCard from '../common/NewsCard';
import AdBanner from '../common/AdBanner';
import ReelsSection from '../homepage/ReelsSection';
import { formatPublishNp } from '../../utils/timeAgo';
import { reelApi } from '../../utils/api';
import { CATEGORY_COLOR, CATEGORY_LABEL } from '../../constants';

// Vivid ko UNIQUE editorial design — homepage Hero jasto haina:
// thulo spotlight card + side stack, tala bento grid, numbered index ra list rows.
function VividhView({ allNews, onOpen }) {
  const [reels, setReels] = useState([]);
  // Sabai bhanda naya thapgareko news mathi (id DESC = add order) —
  // tyasaile spotlight/banner ma sadhai naya samachar dekhinchha
  const news = React.useMemo(
    () => [...(allNews || [])].sort((a, b) => b.id - a.id),
    [allNews]
  );

  useEffect(() => {
    let mounted = true;
    reelApi
      .getAll()
      .then((r) => { if (mounted) setReels(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Content slices
  const spotMain = news[0];
  const spotSide = news.slice(1, 3);
  const gridItems = news.slice(3, 7);
  const sideItems = news.slice(7, 13);
  const listItems = news.slice(13, 18);

  const chip = (item) => {
    const label = CATEGORY_LABEL[item.category_slug] || 'समाचार';
    const color = CATEGORY_COLOR[item.category_slug] || 'var(--crimson)';
    return (
      <span className="inline-flex items-center text-[10.5px] font-bold px-2.5 py-1 rounded-full text-white" style={{ background: color }}>
        {label}
      </span>
    );
  };

  return (
    <main className="max-w-[1240px] mx-auto px-5 pt-7 pb-14 w-full">
      <PageHeader
        title="विविध"
        subtitle="जीवनशैली, कथा र विशेष सामग्रीका ताजा संगालो।"
        crumbs={['गृहपृष्ठ', 'विविध']}
      />

      {/* १. Spotlight — thulo katha + side ma 2 wata stack card */}
      <section className="grid gap-5 lg:gap-6 lg:grid-cols-[1.55fr_1fr] items-stretch mb-10">
        {spotMain && (
          <article
            className="relative rounded-[16px] overflow-hidden cursor-pointer group min-h-[280px] sm:min-h-[380px]"
            onClick={() => onOpen(spotMain)}
            role="button"
            tabIndex={0}
          >
            <img
              src={spotMain.image || 'https://via.placeholder.com/1200x800?text=Kalo+Jhola'}
              alt={spotMain.title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
            <div className="absolute left-0 right-0 bottom-0 p-5 sm:p-6">
              {chip(spotMain)}
              <h2 className="font-display text-white text-[clamp(19px,2.6vw,27px)] font-bold leading-tight mt-2.5 transition-colors group-hover:underline">
                {spotMain.title}
              </h2>
              <div className="flex items-center gap-2 text-white/80 text-[12px] mt-2.5">
                <Clock size={12} /> {formatPublishNp(spotMain.created_at || spotMain.date)}
              </div>
            </div>
          </article>
        )}

        <div className="flex flex-col gap-5">
          {spotSide.map((item) => (
            <article
              key={item.id}
              className="group flex gap-4 bg-white border border-line rounded-[16px] p-4 flex-1 min-h-[130px] cursor-pointer hover:border-crimson hover:shadow-sm transition-all"
              onClick={() => onOpen(item)}
              role="button"
              tabIndex={0}
            >
              <div className="w-28 sm:w-36 flex-shrink-0 rounded-xl overflow-hidden bg-paper-tint">
                <img
                  src={item.image || 'https://via.placeholder.com/320x240?text=Kalo+Jhola'}
                  alt={item.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="min-w-0 flex flex-col justify-center">
                <span className="mb-1.5">{chip(item)}</span>
                <h3 className="text-[14.5px] font-semibold leading-snug line-clamp-3 transition-colors group-hover:text-blue">
                  {item.title}
                </h3>
                <span className="flex items-center gap-1.5 text-[11.5px] text-muted mt-2">
                  <Clock size={11} /> {formatPublishNp(item.created_at || item.date)}
                </span>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* २. Bento grid + numbered index sidebar */}
      <div className="grid gap-8 lg:grid-cols-[1fr_320px] items-start mb-10">
        <div className="grid gap-6 sm:grid-cols-2 min-w-0">
          {gridItems.map((item, i) => (
            <NewsCard key={item.id} item={item} size={i === 0 ? 'lg' : 'md'} onOpen={onOpen} />
          ))}
        </div>

        <aside className="bg-white border border-line rounded-[14px] p-5 shadow-sm min-w-0">
          <h4 className="flex items-center gap-2 text-[15px] font-bold mb-3 border-b border-line pb-3">
            <span className="w-1.5 h-4 bg-crimson rounded-full" /> सूचकाङ्क
          </h4>
          <ol className="flex flex-col">
            {(sideItems.length ? sideItems : news.slice(0, 5)).map((n, i) => (
              <li
                key={n.id}
                className="flex gap-3 py-3 border-b border-line last:border-none last:pb-0 cursor-pointer group"
                onClick={() => onOpen(n)}
                role="button"
                tabIndex={0}
              >
                <span className="font-display text-[26px] font-bold leading-none text-paper-tint [-webkit-text-stroke:1.3px_#2457A8] flex-shrink-0 group-hover:[-webkit-text-stroke-color:#C81E3A] transition-all">
                  {String(i + 1).padStart(2, '०')}
                </span>
                <div className="min-w-0">
                  <p className="text-[13.5px] font-semibold leading-snug line-clamp-2 transition-colors group-hover:text-blue">{n.title}</p>
                  <span className="text-[11px] text-muted mt-1 block">{formatPublishNp(n.created_at || n.date)}</span>
                </div>
              </li>
            ))}
          </ol>
        </aside>
      </div>

      {/* ३. Compact rows */}
      {listItems.map((item) => (
        <article
          key={item.id}
          className="group flex items-center gap-4 bg-white border border-line rounded-xl px-4 py-3 mb-3 cursor-pointer hover:border-crimson hover:shadow-sm transition-all"
          onClick={() => onOpen(item)}
          role="button"
          tabIndex={0}
        >
          <span className="w-1.5 h-8 rounded-full bg-paper-tint group-hover:bg-crimson transition-colors flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <h3 className="font-display font-semibold text-[15.5px] leading-snug transition-colors group-hover:text-blue line-clamp-1">
              {item.title}
            </h3>
            <div className="flex items-center gap-1.5 text-xs text-muted mt-1">
              <Clock size={11} /> {formatPublishNp(item.created_at || item.date)}
            </div>
          </div>
          <div className="w-20 h-14 sm:w-24 sm:h-16 flex-shrink-0 rounded-lg overflow-hidden bg-paper-tint hidden sm:block">
            <img
              src={item.image || 'https://via.placeholder.com/200x140?text=Kalo+Jhola'}
              alt={item.title}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </div>
        </article>
      ))}

      <AdBanner position="infeed" noFallback className="mb-10" />

      {/* ४. Reels — title text chhaina, reels matra */}
      <ReelsSection reels={reels} />
    </main>
  );
}

export default VividhView;
